import { RollbackOutlined } from '@ant-design/icons'
import { Col, Form, Input } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSFormItemList from 'components/CIMSFormItemList'
import type { CIMSFormItemListButtonOptions } from 'components/CIMSFormItemList/types'
import UserSelect from 'components/CIMSSelect/common/UserSelect'
import { useFieldUsers } from '../hooks/useFieldUsers'
import type { ApprovalsWorkflowField, EditFieldOptionFormValue } from '../types'

type ApprovalOptionsFormItemsProps = {
  field: ApprovalsWorkflowField
}

/**
 * Manages approval-option labels, tooltips, approvers, and active state.
 */
const ApprovalOptionsFormItems: React.VFC<ApprovalOptionsFormItemsProps> = ({
  field,
}) => {
  const { toApproverValues } = useFieldUsers()
  const formOptions =
    (Form.useWatch('options') as EditFieldOptionFormValue[] | undefined) ?? []
  const activeOptionIds = new Set(
    formOptions.flatMap(({ optionId }) => optionId ?? [])
  )
  const removedOptions = field.settings.options.filter(
    ({ id }) => !activeOptionIds.has(id)
  )
  const customLabel = field.settings.customizeOptionLabel
  const label =
    customLabel?.enabled === true
      ? { singular: customLabel.singular, plural: customLabel.plural }
      : { singular: 'Option', plural: 'Options' }
  const buttons: CIMSFormItemListButtonOptions<EditFieldOptionFormValue>[] = [
    {
      icon: 'add',
      add: () => ({
        approverIds: [],
        label: '',
        tooltip: null,
      }),
    },
    {
      type: 'dropdown',
      icon: <RollbackOutlined />,
      label: label.singular,
      labelPrefix: 'Restore ',
      items: removedOptions.map(({ id, label }) => ({ key: id, label })),
      add: (optionId) => {
        const option = removedOptions.find(({ id }) => id === optionId)
        return option
          ? {
              approverIds: toApproverValues(option.approverIds),
              label: option.label,
              optionId: option.id,
              tooltip: option.tooltip,
            }
          : undefined
      },
    },
  ]

  return (
    <CIMSFormItemList
      buttons={buttons}
      label={label}
      name="options"
      rowOptions={{ showDeleteAfter: 0 }}
    >
      {(number) => (
        <>
          <CIMSFormItem name={[number, 'optionId']} noStyle>
            <Input type="hidden" />
          </CIMSFormItem>
          <Col xs={24} xl={12}>
            <CIMSFormItem
              label="Label"
              name={[number, 'label']}
              requiredMessage="Please enter a label"
            >
              <Input />
            </CIMSFormItem>
          </Col>
          <Col xs={24} xl={12}>
            <CIMSFormItem label="Tooltip" name={[number, 'tooltip']}>
              <Input allowClear />
            </CIMSFormItem>
          </Col>
          <Col xs={24}>
            <CIMSFormItem label="Approvers" name={[number, 'approverIds']}>
              <UserSelect mode="multiple" />
            </CIMSFormItem>
          </Col>
        </>
      )}
    </CIMSFormItemList>
  )
}

export default ApprovalOptionsFormItems
